import React, { useEffect, useRef } from 'react';
import MonacoEditor from 'react-monaco-editor';
import * as monaco from 'monaco-editor';
import type * as monacoTypes from 'monaco-editor';
import { v4 as uuidv4 } from 'uuid';
import { css } from '@emotion/css';
import { installAutoHeightLayout } from '../shared/autoHeightLayout';
import { hideMirroredInputAreaClassName } from '../shared/hideMirroredInputArea';
import { languageConfiguration } from './loki';
import { buildMonarchLanguage } from './monarch/buildLanguage';
import { getLokiCompletionProvider } from './completion/getCompletionProvider';
import type { LokiEditorProps } from './types';

export type { LokiEditorProps, LabelMatcher } from './types';

const LANG_ID = 'loki';

const SIZE_MAP: Record<string, { className: string; minHeight: number }> = {
  small: { className: 'ant-input-sm', minHeight: 24 },
  middle: { className: 'ant-input-md', minHeight: 32 },
  large: { className: 'ant-input-lg', minHeight: 40 },
};

const themeMap: Record<string, string> = {
  light: 'loki-light',
  dark: 'loki-dark',
};

const containerDisabledClassName = css`
  .monaco-editor {
    user-select: none;
    pointer-events: none;
  }
`;

const containerReadOnlyClassName = css`
  .monaco-editor .cursors-layer > .cursor {
    opacity: 0 !important;
  }
`;

let languageRegistered = false;

function ensureLanguage() {
  if (languageRegistered) return;
  if (!monaco.languages.getLanguages().some((lang) => lang.id === LANG_ID)) {
    monaco.languages.register({ id: LANG_ID });
    monaco.languages.setLanguageConfiguration(LANG_ID, languageConfiguration);
    monaco.languages.setMonarchTokensProvider(LANG_ID, buildMonarchLanguage());
  }
  languageRegistered = true;
}

let themesEnsured = false;

function ensureThemes() {
  if (themesEnsured) return;
  themesEnsured = true;
  monaco.editor.defineTheme('loki-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword.pipeline', foreground: '795e26' },
      { token: 'keyword.function', foreground: '795e26', fontStyle: 'bold' },
      { token: 'keyword.aggregation', foreground: '0070c1' },
      { token: 'keyword', foreground: '0070c1' },
      { token: 'operator.pipe', foreground: 'b8860b', fontStyle: 'bold' },
      { token: 'operator', foreground: 'b8860b' },
      { token: 'type.duration', foreground: '098658' },
      { token: 'type.bytes', foreground: '098658' },
      { token: 'string', foreground: 'a31515' },
      { token: 'number', foreground: '098658' },
      { token: 'number.float', foreground: '098658' },
      { token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
      { token: 'delimiter.curly', foreground: '0070c1' },
    ],
    colors: {
      'editor.background': '#00000000',
      focusBorder: '#00000000',
    },
  });
  monaco.editor.defineTheme('loki-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword.pipeline', foreground: 'dcdcaa' },
      { token: 'keyword.function', foreground: 'dcdcaa', fontStyle: 'bold' },
      { token: 'keyword.aggregation', foreground: '9cdcfe' },
      { token: 'keyword', foreground: '9cdcfe' },
      { token: 'operator.pipe', foreground: 'ffd700', fontStyle: 'bold' },
      { token: 'operator', foreground: 'ffd700' },
      { token: 'type.duration', foreground: '6a9955' },
      { token: 'type.bytes', foreground: '6a9955' },
      { token: 'string', foreground: 'ce9178' },
      { token: 'number', foreground: 'b5cea8' },
      { token: 'number.float', foreground: 'b5cea8' },
      { token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
      { token: 'delimiter.curly', foreground: '9cdcfe' },
    ],
    colors: {
      'editor.background': '#00000000',
      focusBorder: '#00000000',
    },
  });
}

/* ---------- Global keybinding rules registered once ---------- */
let globalKeybindingDisposables: monaco.IDisposable[] | null = null;

function ensureGlobalKeybindings() {
  if (globalKeybindingDisposables) return;
  globalKeybindingDisposables = [
    monaco.editor.addKeybindingRule({
      keybinding: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF,
      command: null,
    }),
    monaco.editor.addKeybindingRule({
      keybinding: monaco.KeyCode.Enter,
      command: '-',
      when: '!suggestWidgetVisible',
    }),
  ];
}

function disposeGlobalKeybindings() {
  if (!globalKeybindingDisposables) return;
  globalKeybindingDisposables.forEach((d) => d.dispose());
  globalKeybindingDisposables = null;
}

const LokiMonacoEditor: React.FC<LokiEditorProps> = (props) => {
  const id = uuidv4();
  const {
    className,
    maxHeight,
    fontSize,
    size = 'middle',
    theme = 'light',
    value = '',
    placeholder,
    enableAutocomplete = true,
    readOnly = false,
    disabled = false,
    variables,
    onChange,
    onEnter,
    onBlur,
    onFocus,
    editorDidMount,
    fetchLabelNames,
    fetchLabelValues,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monacoTypes.editor.IStandaloneCodeEditor | null>(null);
  const disposablesRef = useRef<monaco.IDisposable[]>([]);

  const variablesRef = useRef<string[]>(variables ?? []);
  const fetchLabelNamesRef = useRef<typeof fetchLabelNames>(fetchLabelNames);
  const fetchLabelValuesRef = useRef<typeof fetchLabelValues>(fetchLabelValues);

  useEffect(() => {
    variablesRef.current = variables ?? [];
  }, [variables]);
  useEffect(() => {
    fetchLabelNamesRef.current = fetchLabelNames;
  }, [fetchLabelNames]);
  useEffect(() => {
    fetchLabelValuesRef.current = fetchLabelValues;
  }, [fetchLabelValues]);

  ensureLanguage();

  useEffect(() => {
    if (!enableAutocomplete) return;
    const disposable = monaco.languages.registerCompletionItemProvider(
      LANG_ID,
      getLokiCompletionProvider({
        variablesRef,
        fetchLabelNamesRef,
        fetchLabelValuesRef,
        editorRef,
      }),
    );
    disposablesRef.current.push(disposable);
    return () => {
      disposable.dispose();
      disposablesRef.current = disposablesRef.current.filter((d) => d !== disposable);
    };
  }, [enableAutocomplete]);

  useEffect(() => {
    ensureGlobalKeybindings();
    return () => {
      disposeGlobalKeybindings();
      disposablesRef.current.forEach((d) => d.dispose());
      disposablesRef.current = [];
    };
  }, []);

  const handleEditorMount = (editor: monacoTypes.editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;
    ensureThemes();

    const isEditorFocused = editor.createContextKey<boolean>('isEditorFocused' + id, false);
    editor.onDidBlurEditorWidget(() => {
      isEditorFocused.set(false);
      onBlur?.(editor.getValue());
      const position = editor.getPosition();
      if (position) {
        editor.setSelection(new monaco.Selection(position.lineNumber, position.column, position.lineNumber, position.column));
      }
    });
    editor.onDidFocusEditorText(() => {
      isEditorFocused.set(true);
      onFocus?.(editor.getValue());
    });

    if (containerRef.current) {
      installAutoHeightLayout(editor, containerRef.current, { heightStyleProperty: 'minHeight' });
    }

    // Shift+Enter → newline
    editor.addCommand(
      monaco.KeyMod.Shift | monaco.KeyCode.Enter,
      () => {
        const position = editor.getPosition();
        if (position) {
          editor.executeEdits('shift-enter', [
            {
              range: new monaco.Range(position.lineNumber, position.column, position.lineNumber, position.column),
              text: '\n',
            },
          ]);
          editor.setPosition({ lineNumber: position.lineNumber + 1, column: 1 });
        }
      },
      'isEditorFocused' + id,
    );

    // Enter → execute query
    editor.addCommand(
      monaco.KeyCode.Enter,
      () => {
        onEnter?.(editor.getValue());
      },
      '!suggestWidgetVisible && isEditorFocused' + id,
    );

    // Trigger suggest when `$` is typed
    const model = editor.getModel();
    if (model) {
      const changeDisposable = model.onDidChangeContent((e) => {
        const insertedDollar = e.changes.some((c) => c.text === '$');
        if (insertedDollar) {
          setTimeout(() => editor.trigger('loki', 'editor.action.triggerSuggest', {}), 0);
        }
      });
      disposablesRef.current.push(changeDisposable);
    }

    editorDidMount?.(editor);
  };

  const handleChange = (newValue: string) => {
    onChange?.(newValue);
  };

  const themeValue = themeMap[theme];

  return (
    <div
      className={
        'ant-input' +
        (size ? ` ${SIZE_MAP[size].className}` : '') +
        (disabled ? ` ant-input-disabled ${containerDisabledClassName}` : '') +
        (readOnly ? ` ${containerReadOnlyClassName}` : '') +
        ` ${hideMirroredInputAreaClassName}` +
        (className ? ` ${className}` : '')
      }
      style={{
        display: 'block',
        resize: 'vertical',
        overflow: 'auto',
        position: 'relative',
        minHeight: SIZE_MAP[size].minHeight,
        maxHeight,
      }}
    >
      <div ref={containerRef} style={{ height: '100%' }}>
        <MonacoEditor
          width='100%'
          height='100%'
          language={LANG_ID}
          theme={themeValue}
          value={value}
          onChange={handleChange}
          editorDidMount={handleEditorMount}
          options={{
            editContext: false,
            // 使用浏览器原生右键菜单，替代 monaco 命令菜单
            contextmenu: false,
            minimap: { enabled: false },
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            autoIndent: 'full',
            readOnly: readOnly || disabled,
            scrollBeyondLastLine: false,
            smoothScrolling: true,
            fontSize,
            tabSize: 2,
            wordWrap: 'on',
            fixedOverflowWidgets: true,
            glyphMargin: false,
            lineNumbers: 'off',
            lineNumbersMinChars: 0,
            folding: false,
            lineDecorationsWidth: 0,
            overviewRulerBorder: false,
            overviewRulerLanes: 0,
            placeholder: placeholder,
            renderLineHighlight: 'none',
            occurrencesHighlight: 'off',
            scrollbar: {
              vertical: 'hidden',
              horizontal: 'hidden',
              alwaysConsumeMouseWheel: false,
            },
            suggest: {
              showWords: false,
              filterGraceful: false,
              snippetsPreventQuickSuggestions: false,
              shareSuggestSelections: false,
            },
          }}
        />
      </div>
    </div>
  );
};

export default LokiMonacoEditor;

// Re-export parser and locator for advanced consumers
export { parse, locate } from './parser';
